// src/admincomponents/SystemDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../api/apiClient'; //
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import styled from 'styled-components';
import { Panel, PanelHeader, PanelTitle, ErrorNote } from './adminStyles';
import { color, font } from '../../theme/tokens';

// --- 样式定义 ---
const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(${({ $min = 360 }) => $min}px, 100%), 1fr));
    gap: 16px;
    width: 100%;
`;

const Widget = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 20px;
    background: rgba(7, 2, 5, 0.5);
    border: 1px solid ${color.line};
`;

const WidgetTitle = styled.h3`
    margin: 0 0 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${color.line};
    font-family: ${font.display};
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.18em;
    color: ${color.text};
`;

const LogBox = styled.div`
    height: 300px;
    overflow-y: auto;
    padding: 10px 12px;
    background: ${color.void};
    border: 1px solid ${color.line};
    font-family: ${font.mono};
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
`;

const LogMeta = styled.span`
    color: ${color.text3};
`;

const Stat = styled.div`
    text-align: center;
    h4 { margin: 0 0 8px; font-weight: 400; font-size: 13px; letter-spacing: 0.2em; color: ${color.text3}; }
    p { margin: 0; font-family: ${font.mono}; font-size: 32px; font-weight: 500; color: ${color.textBright}; }
    span { margin-left: 4px; font-size: 14px; color: ${color.text2}; }
`;

const ConnState = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    letter-spacing: 0.1em;
    color: ${({ $on }) => ($on ? color.text : color.bloodHi)};
    &::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${({ $on }) => ($on ? color.denpa : color.bloodHi)};
    }
`;

const getLogColor = (level) => {
    switch (level) {
        case 'critical': return color.bloodHi;
        case 'error': return color.bloodHi;
        case 'warn': return color.text;
        default: return color.text2;
    }
};

// --- 图表配置 ---
const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
    },
    scales: {
        x: { ticks: { color: color.text3 }, grid: { color: color.line } },
        y: { ticks: { color: color.text3, font: { family: 'JetBrains Mono' } }, grid: { color: color.line }, suggestedMax: 100, beginAtZero: true },
    },
};


// --- 子组件 ---
const StatCard = ({ title, value, unit }) => (
    <Stat>
        <h4>{title}</h4>
        <p>{value}<span>{unit}</span></p>
    </Stat>
);

const LogViewer = ({ logs, title }) => {
    const endOfLogsRef = useRef(null);
    useEffect(() => {
        endOfLogsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [logs]);

    return (
        <Widget>
            <WidgetTitle>{title}</WidgetTitle>
            <LogBox>
                {logs.map((log, index) => (
                    <div key={index} style={{ color: getLogColor(log.level), marginBottom: '4px' }}>
                        <LogMeta>{new Date(log.timestamp).toLocaleTimeString()} [{log.container || 'App'}]</LogMeta>: {log.message}
                    </div>
                ))}
                <div ref={endOfLogsRef} />
            </LogBox>
        </Widget>
    );
};


// --- 主组件 ---
function SystemDashboard() {
    const [stats, setStats] = useState({ cpu: { load: 0 }, memory: { usage: 0 } });
    const [cpuHistory, setCpuHistory] = useState([]);
    const [memHistory, setMemHistory] = useState([]);
    const [userActivity, setUserActivity] = useState({ totalUsers: 'N/A', recentRegistrations: [] });
    const [dockerLogs, setDockerLogs] = useState([]);
    const [appLogs, setAppLogs] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // EventSource 会自动处理 cookie，所以我们的 httpOnly cookie 会被发送
        const eventSource = new EventSource(`${apiClient.defaults.baseURL}/admin/monitoring/stream`);

        eventSource.onopen = () => {
            console.log("SSE connection established.");
            setIsConnected(true);
            setError('');
        };

        eventSource.onerror = (e) => {
            console.error("SSE connection error:", e);
            if (e.target.readyState === EventSource.CLOSED) {
                 setError('连接已断开，请检查您的权限或服务器状态。');
            } else {
                 setError('连接中发生错误...');
            }
            setIsConnected(false);
        };

        const addDataPoint = (history, data) => {
            const newHistory = [...history, data];
            return newHistory.length > 30 ? newHistory.slice(1) : newHistory; // Keep last 30 points
        };

        eventSource.addEventListener('server-stats', (e) => {
            const data = JSON.parse(e.data);
            setStats(data);
            setCpuHistory(prev => addDataPoint(prev, parseFloat(data.cpu.load)));
            setMemHistory(prev => addDataPoint(prev, parseFloat(data.memory.usage)));
        });

        eventSource.addEventListener('user-activity', (e) => {
            setUserActivity(JSON.parse(e.data));
        });

        const addLog = (setLogs, log) => {
             setLogs(prev => {
                const newLogs = [...prev, log];
                return newLogs.length > 100 ? newLogs.slice(1) : newLogs; // Keep last 100 logs
             });
        }

        eventSource.addEventListener('docker-log', (e) => {
            addLog(setDockerLogs, JSON.parse(e.data));
        });

        eventSource.addEventListener('app-log', (e) => {
            addLog(setAppLogs, JSON.parse(e.data));
        });


        // Cleanup on component unmount
        return () => {
            console.log("Closing SSE connection.");
            eventSource.close();
        };

    }, []); // 空依赖数组确保 effect 只运行一次

    const chartData = (label, data, rgb) => ({
        labels: Array(data.length).fill(''),
        datasets: [{
            label,
            data,
            fill: true,
            backgroundColor: `rgba(${rgb}, 0.14)`,
            borderColor: `rgba(${rgb}, 1)`,
            borderWidth: 1.5,
            tension: 0.3,
            pointRadius: 0,
        }]
    });

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle>系统仪表盘</PanelTitle>
                <ConnState $on={isConnected} role="status">
                    {isConnected ? '已连接' : '未连接'}
                </ConnState>
            </PanelHeader>
            {!isConnected && error && <ErrorNote role="alert">{error}</ErrorNote>}

            {/* --- Stats Grid --- */}
            <Grid $min={200} style={{ marginBottom: '16px' }}>
                <Widget><StatCard title="CPU 负载" value={stats.cpu.load} unit="%" /></Widget>
                <Widget><StatCard title="内存占用" value={stats.memory.usage} unit="%" /></Widget>
                <Widget><StatCard title="磁盘占用" value={stats.disk?.usage || 'N/A'} unit="%" /></Widget>
                <Widget><StatCard title="用户总数" value={userActivity.totalUsers} unit="" /></Widget>
            </Grid>

            {/* --- Charts Grid --- */}
            <Grid style={{ marginBottom: '16px' }}>
                <Widget>
                    <WidgetTitle>CPU 负载曲线</WidgetTitle>
                    <div style={{ height: '250px' }}>
                        <Line data={chartData('CPU Load', cpuHistory, '200, 50, 62')} options={chartOptions} />
                    </div>
                </Widget>
                <Widget>
                    <WidgetTitle>内存占用曲线</WidgetTitle>
                    <div style={{ height: '250px' }}>
                        <Line data={chartData('Memory Usage', memHistory, '184, 166, 167')} options={chartOptions} />
                    </div>
                </Widget>
            </Grid>

            {/* --- Logs Grid --- */}
            <Grid>
                <LogViewer logs={appLogs} title="应用日志" />
                <LogViewer logs={dockerLogs} title="Docker 容器日志" />
            </Grid>
        </Panel>
    );
}

export default SystemDashboard;
